(function () {
  "use strict";

  class MobileMenuController {
    constructor(root) {
      this.root = root;
      this.holder = root.closest(".mobile-screen-menu-holder");
      this.panel = root.querySelector(".mobile-menu__panel");
      this.overlay = root.querySelector("[data-mobile-menu-overlay]");
      this.closeButtons = Array.from(root.querySelectorAll("[data-mobile-menu-close]"));
      this.triggers = Array.from(document.querySelectorAll("[data-mobile-menu-trigger]"));
      this.tabs = Array.from(root.querySelectorAll("[data-mobile-menu-tab]"));
      this.panes = Array.from(root.querySelectorAll("[data-mobile-menu-pane]"));
      this.filterControl = root.querySelector("[data-mobile-menu-filter]");
      this.filterToggle = root.querySelector("[data-mobile-menu-filter-toggle]");
      this.filterInput = root.querySelector("[data-mobile-menu-filter-input]");
      this.searchResults = root.querySelector("[data-mobile-menu-search-results]");
      this.searchResultsList = root.querySelector("[data-mobile-menu-search-results-list]");
      this.searchResultsEmpty = root.querySelector("[data-mobile-menu-search-results-empty]");
      this.paneLayer = root.querySelector("[data-mobile-menu-pane-layer]");
      this.searchItems = Array.from(root.querySelectorAll("[data-mobile-menu-search-item]"));
      this.searchEmptyStates = Array.from(root.querySelectorAll("[data-mobile-menu-filter-empty]"));
      this.viewport = root.querySelector("[data-mobile-menu-viewport]");
      this.filterBar = root.querySelector(".mobile-menu__filter-bar");
      this.breadcrumbSearchUrl =
        root.getAttribute("data-mobile-menu-breadcrumb-search-url") || "";
      this.paneMap = new Map(this.panes.map((pane) => [pane.id, pane]));
      this.paneParentMap = new Map();
      this.rootPaneIds = new Set(
        this.panes
          .filter((pane) => pane.hasAttribute("data-mobile-menu-root-pane"))
          .map((pane) => pane.id)
      );
      this.tabIndexMap = new Map(
        this.tabs.map((tab, index) => [tab.getAttribute("data-mobile-menu-tab"), index])
      );
      this.scroller = document.querySelector(".scroller");
      this.defaultRootPaneId =
        this.tabs[0]?.getAttribute("data-mobile-menu-tab") ||
        this.panes.find((pane) => pane.hasAttribute("data-mobile-menu-root-pane"))?.id ||
        null;
      this.activeRootPaneId = this.defaultRootPaneId;
      this.activePaneId = this.defaultRootPaneId;
      this.paneStack = this.activePaneId ? [this.activePaneId] : [];
      this.transitionTimeoutId = null;
      this.transitionFrameId = null;
      this.transitionRunId = 0;
      this.closeTimeoutId = null;
      this.isOpen = false;
      this.isClosing = false;
      this.activeTrigger = null;
      this.focusableSelectors = [
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        "[tabindex]:not([tabindex='-1'])",
      ].join(", ");

      this.boundOnKeydown = this.onKeydown.bind(this);
      this.boundSyncOffset = this.syncOffset.bind(this);
      this.boundOnDocumentClick = this.onDocumentClick.bind(this);

      this.panes.forEach((pane) => {
        const backButton = pane.querySelector("[data-mobile-menu-back]");
        const parentPaneId = backButton?.getAttribute("data-mobile-menu-back");
        if (parentPaneId) {
          this.paneParentMap.set(pane.id, parentPaneId);
        }
      });

      this.searchItems.forEach((item) => {
        item.dataset.searchNormalized = this.normalizeSearchValue(item.getAttribute("data-search") || "");
      });

      this.searchEntries = this.searchItems
        .map((item) => this.buildSearchEntry(item))
        .filter(Boolean);
      this.searchEntryPathMap = this.buildSearchEntryPathMap();

      this.bindEvents();
      this.syncOffset();
      this.finalizePaneStates();
      this.updateSearchVisibility();
      this.updateSearchPlaceholder();
      this.enhanceSearchEntriesWithBreadcrumbAliases();
    }

    bindEvents() {
      this.triggers.forEach((trigger) => {
        trigger.addEventListener("click", (event) => {
          event.preventDefault();
          this.toggle(trigger);
        });
      });

      this.tabs.forEach((tab) => {
        tab.addEventListener("click", (event) => {
          event.preventDefault();
          this.activateTopLevel(tab.getAttribute("data-mobile-menu-tab"));
        });
      });

      if (this.overlay) {
        this.overlay.addEventListener("click", () => this.close());
      }

      this.closeButtons.forEach((button) => {
        button.addEventListener("click", () => this.close());
      });

      if (this.filterToggle) {
        this.filterToggle.addEventListener("click", (event) => {
          event.preventDefault();

          if (
            this.filterControl?.classList.contains("is-expanded") &&
            !this.normalizeSearchValue(this.filterInput?.value || "")
          ) {
            this.collapseSearch({ blur: true, force: true });
            return;
          }

          this.expandSearch({ focus: true });
        });
      }

      if (this.filterControl) {
        this.filterControl.addEventListener("click", (event) => {
          if (
            event.target.closest("[data-mobile-menu-filter-toggle]") ||
            event.target.closest("[data-mobile-menu-filter-input]")
          ) {
            return;
          }

          this.expandSearch({ focus: true });
        });
      }

      if (this.filterInput) {
        this.filterInput.addEventListener("focus", () => {
          this.expandSearch();
        });

        this.filterInput.addEventListener("input", () => {
          this.expandSearch();
          this.applySearchFilter();
        });

        this.filterInput.addEventListener("keydown", (event) => {
          if ((event.key || "").toLowerCase() !== "escape") return;

          event.preventDefault();

          if (this.normalizeSearchValue(this.filterInput.value)) {
            this.filterInput.value = "";
            this.applySearchFilter();
            return;
          }

          this.collapseSearch({ blur: true, force: true });
        });

        this.filterInput.addEventListener("blur", () => {
          window.requestAnimationFrame(() => {
            if (
              !this.filterControl?.contains(document.activeElement) &&
              !this.normalizeSearchValue(this.filterInput?.value || "")
            ) {
              this.collapseSearch({ force: true });
            }
          });
        });
      }

      this.root.addEventListener("click", (event) => {
        const searchPaneTarget = event.target.closest("[data-mobile-menu-search-target]");
        const searchLink = event.target.closest("[data-mobile-menu-search-link]");
        const drilldown = event.target.closest("[data-mobile-menu-target]");
        const backButton = event.target.closest("[data-mobile-menu-back]");

        if (searchPaneTarget) {
          event.preventDefault();
          this.openSearchResultPane(searchPaneTarget.getAttribute("data-mobile-menu-search-target"));
          return;
        }

        if (searchLink) {
          this.resetSearch();
          return;
        }

        if (drilldown) {
          event.preventDefault();
          this.openPane(drilldown.getAttribute("data-mobile-menu-target"));
          return;
        }

        if (backButton) {
          event.preventDefault();
          this.goBack(backButton.getAttribute("data-mobile-menu-back"));
        }
      });

      window.addEventListener("resize", this.boundSyncOffset, { passive: true });
      window.addEventListener("orientationchange", this.boundSyncOffset);
    }

    toggle(trigger) {
      if (this.isOpen) {
        this.close();
      } else {
        this.open(trigger);
      }
    }

    open(trigger) {
      if (this.closeTimeoutId) {
        window.clearTimeout(this.closeTimeoutId);
        this.closeTimeoutId = null;
      }

      this.activeTrigger = trigger || this.activeTrigger;
      this.closeCompetingUI();
      this.resetMenu();
      this.syncOffset();

      this.isOpen = true;
      this.isClosing = false;
      this.holder.classList.remove("is-closing");
      this.holder.classList.add("expanded");
      this.root.setAttribute("aria-hidden", "false");
      document.body.classList.add("menu-open", "mobile-menu-open");
      this.scroller?.classList.add("overflow-lock");
      this.updateTriggerState(true);

      document.addEventListener("keydown", this.boundOnKeydown);
      document.addEventListener("click", this.boundOnDocumentClick, true);

      window.requestAnimationFrame(() => {
        const initialFocus = this.root.querySelector("[data-mobile-menu-close]") || this.panel;
        initialFocus.focus();
      });
    }

    close(options = {}) {
      if (!this.isOpen || this.isClosing) return;

      const { returnFocus = true } = options;

      this.isOpen = false;
      this.isClosing = true;
      this.holder.classList.add("is-closing");
      this.holder.classList.remove("expanded");
      document.body.classList.remove("menu-open", "mobile-menu-open");
      this.scroller?.classList.remove("overflow-lock");
      this.updateTriggerState(false);

      document.removeEventListener("keydown", this.boundOnKeydown);
      document.removeEventListener("click", this.boundOnDocumentClick, true);

      this.closeTimeoutId = window.setTimeout(() => {
        this.closeTimeoutId = null;
        this.isClosing = false;
        this.holder.classList.remove("is-closing");
        this.root.setAttribute("aria-hidden", "true");
        this.resetMenu();

        if (returnFocus && this.activeTrigger) {
          this.activeTrigger.focus();
        }
      }, 260);
    }

    closeImmediate() {
      if (this.closeTimeoutId) {
        window.clearTimeout(this.closeTimeoutId);
        this.closeTimeoutId = null;
      }

      this.isOpen = false;
      this.isClosing = false;
      this.holder.classList.remove("expanded", "is-closing");
      this.root.setAttribute("aria-hidden", "true");
      document.body.classList.remove("menu-open", "mobile-menu-open");
      this.scroller?.classList.remove("overflow-lock");
      this.updateTriggerState(false);

      document.removeEventListener("keydown", this.boundOnKeydown);
      document.removeEventListener("click", this.boundOnDocumentClick, true);

      this.resetMenu();
    }

    openPane(targetId) {
      const targetPane = this.paneMap.get(targetId);
      if (!targetPane || targetId === this.activePaneId) return;

      const topLevelPaneId =
        targetPane.getAttribute("data-top-level") || targetId;
      const nextPaneStack = [...this.paneStack, targetId];

      this.navigateToPane(targetId, {
        direction: "forward",
        topLevelPaneId,
        nextPaneStack,
      });
    }

    goBack(targetId) {
      if (!targetId || targetId === this.activePaneId) return;

      const targetPane = this.paneMap.get(targetId);
      const topLevelPaneId =
        targetPane?.getAttribute("data-top-level") || targetId;
      const existingIndex = this.paneStack.indexOf(targetId);
      let nextPaneStack;

      if (existingIndex === -1) {
        nextPaneStack = topLevelPaneId ? [topLevelPaneId] : [];
      } else {
        nextPaneStack = this.paneStack.slice(0, existingIndex + 1);
      }

      this.navigateToPane(targetId, {
        direction: "back",
        topLevelPaneId,
        nextPaneStack,
      });
    }

    activateTopLevel(targetId) {
      if (!targetId || !this.rootPaneIds.has(targetId)) return;

      if (targetId === this.activeRootPaneId) {
        const activeTab = this.tabs.find(
          (tab) => tab.getAttribute("data-mobile-menu-tab") === targetId
        );
        activeTab?.focus();
        return;
      }

      const currentRootPaneId = this.activeRootPaneId;
      const currentIndex = this.tabIndexMap.get(currentRootPaneId);
      const targetIndex = this.tabIndexMap.get(targetId);
      const direction =
        typeof currentIndex === "number" &&
        typeof targetIndex === "number" &&
        targetIndex < currentIndex
          ? "back"
          : "forward";

      this.resetSearch();

      this.transitionBetweenPanes(currentRootPaneId, targetId, {
        direction,
        nextRootPaneId: targetId,
        nextPaneStack: [targetId],
        resetAllScroll: true,
        updateTabsImmediately: true,
      });

      this.updateSearchVisibility();
      this.updateSearchPlaceholder();

      const activeTab = this.tabs.find(
        (tab) => tab.getAttribute("data-mobile-menu-tab") === targetId
      );
      activeTab?.focus();
    }

    navigateToPane(targetId, options = {}) {
      const targetPane = this.paneMap.get(targetId);
      if (!targetPane) return;

      const {
        direction = "forward",
        topLevelPaneId = targetPane.getAttribute("data-top-level") || targetId,
        nextPaneStack = [targetId],
        resetAllScroll = false,
      } = options;

      this.resetSearch();

      this.transitionBetweenPanes(this.activePaneId, targetId, {
        direction,
        nextRootPaneId: topLevelPaneId,
        nextPaneStack,
        resetAllScroll,
      });
    }

    finishPendingTransition(finalize = true) {
      this.transitionRunId += 1;

      if (this.transitionTimeoutId) {
        window.clearTimeout(this.transitionTimeoutId);
        this.transitionTimeoutId = null;
      }

      if (this.transitionFrameId) {
        window.cancelAnimationFrame(this.transitionFrameId);
        this.transitionFrameId = null;
      }

      if (finalize) {
        this.finalizePaneStates();
      }
    }

    transitionBetweenPanes(fromPaneId, targetId, options = {}) {
      const fromPane = this.paneMap.get(fromPaneId);
      const targetPane = this.paneMap.get(targetId);
      if (!fromPane || !targetPane || fromPaneId === targetId) return;

      const {
        direction = "forward",
        nextRootPaneId = targetPane.getAttribute("data-top-level") || targetId,
        nextPaneStack = [targetId],
        resetAllScroll = false,
        updateTabsImmediately = false,
      } = options;

      this.finishPendingTransition();

      if (this.activePaneId !== fromPaneId || this.activeRootPaneId !== this.getRootPaneId(fromPaneId)) {
        this.activeRootPaneId = this.getRootPaneId(fromPaneId);
        this.activePaneId = fromPaneId;
        this.paneStack = this.rootPaneIds.has(fromPaneId) ? [fromPaneId] : [this.activeRootPaneId, fromPaneId];
        this.finalizePaneStates();
      }

      if (resetAllScroll) {
        this.panes.forEach((pane) => this.resetPaneScroll(pane));
      }

      if (this.rootPaneIds.has(fromPaneId) && this.activePaneId !== fromPaneId) {
        this.activePaneId = fromPaneId;
        this.paneStack = [fromPaneId];
        this.finalizePaneStates();
      }

      this.activeRootPaneId = nextRootPaneId;
      this.paneStack = nextPaneStack;

      if (updateTabsImmediately) {
        this.updateTabState();
      }

      this.animateToPane(fromPaneId, targetId, direction);
    }

    animateToPane(fromPaneId, targetId, direction) {
      const currentPane = this.paneMap.get(fromPaneId);
      const targetPane = this.paneMap.get(targetId);
      const runId = this.transitionRunId;

      if (!currentPane || !targetPane) return;

      if (currentPane && currentPane !== targetPane) {
        currentPane.classList.remove("is-left", "is-right");
        currentPane.classList.add(direction === "forward" ? "is-left" : "is-right");
        currentPane.classList.remove("is-active");
      }

      currentPane.hidden = false;
      targetPane.hidden = false;
      targetPane.classList.remove("is-left", "is-right");
      targetPane.classList.add(direction === "forward" ? "is-right" : "is-left");

      this.activePaneId = targetId;
      this.root.setAttribute("data-nav-direction", direction);

      this.transitionFrameId = window.requestAnimationFrame(() => {
        if (runId !== this.transitionRunId) return;

        this.transitionFrameId = null;
        targetPane.hidden = false;
        targetPane.classList.add("is-active");
        targetPane.classList.remove("is-left", "is-right");
        this.resetPaneScroll(targetPane);
        this.updateExpandedState();
      });

      this.transitionTimeoutId = window.setTimeout(() => {
        if (runId !== this.transitionRunId) return;

        this.transitionTimeoutId = null;
        this.finalizePaneStates();
      }, 340);
    }

    finalizePaneStates() {
      const activePane = this.paneMap.get(this.activePaneId);

      this.panes.forEach((pane) => {
        const paneRootId = this.getRootPaneId(pane.id);
        const belongsToActiveRoot = paneRootId === this.activeRootPaneId;
        const inStack = belongsToActiveRoot && this.paneStack.includes(pane.id);

        pane.classList.remove("is-left", "is-right", "is-active");

        if (pane === activePane) {
          pane.hidden = false;
          pane.classList.add("is-active");
        } else if (inStack) {
          pane.hidden = false;
          pane.classList.add("is-left");
        } else if (belongsToActiveRoot) {
          pane.hidden = false;
          pane.classList.add("is-right");
        } else {
          pane.hidden = true;
        }
      });

      this.updateTabState();
      this.updateExpandedState();
    }

    updateSearchVisibility() {
      if (!this.filterBar) return;
      const activeTab = this.tabs.find(
        (tab) => tab.getAttribute("data-mobile-menu-tab") === this.activeRootPaneId
      );
      const label = activeTab ? activeTab.textContent.trim() : "";
      const shouldHide = label === "Clearance";
      if (shouldHide) {
        this.resetSearch();
        this.filterBar.classList.add("is-search-hidden");
      } else {
        this.filterBar.classList.remove("is-search-hidden");
      }
    }

    updateSearchPlaceholder() {
      if (!this.filterInput) return;
      const activeTab = this.tabs.find(
        (tab) => tab.getAttribute("data-mobile-menu-tab") === this.activeRootPaneId
      );
      const label = activeTab ? activeTab.textContent.trim() : "";
      this.filterInput.placeholder = label ? `Searching in ${label}` : "Search Collections";
    }

    resetMenu() {
      this.finishPendingTransition(false);
      this.resetSearch();
      this.activeRootPaneId = this.defaultRootPaneId;
      this.activePaneId = this.defaultRootPaneId;
      this.paneStack = this.activePaneId ? [this.activePaneId] : [];
      this.root.setAttribute("data-nav-direction", "forward");
      this.updateSearchVisibility();
      this.updateSearchPlaceholder();

      this.panes.forEach((pane) => {
        this.resetPaneScroll(pane);
      });

      this.finalizePaneStates();
    }

    resetPaneScroll(pane) {
      const scrollContainer = pane?.querySelector("[data-mobile-menu-scroll]");
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }

    updateTabState() {
      this.tabs.forEach((tab) => {
        const isActive =
          tab.getAttribute("data-mobile-menu-tab") === this.activeRootPaneId;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        tab.setAttribute("tabindex", isActive ? "0" : "-1");
      });
    }

    updateExpandedState() {
      const expandedTargets = new Set(this.paneStack.slice(1));

      this.root.querySelectorAll("[data-mobile-menu-target]").forEach((button) => {
        const targetId = button.getAttribute("data-mobile-menu-target");
        button.setAttribute("aria-expanded", expandedTargets.has(targetId) ? "true" : "false");
      });
    }

    updateTriggerState(isExpanded) {
      this.triggers.forEach((trigger) => {
        trigger.setAttribute("aria-expanded", isExpanded ? "true" : "false");
      });
    }

    closeCompetingUI() {
      if (window.MobileSearchOverlay?.isOpen()) {
        window.MobileSearchOverlay.close();
      }

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer?.classList.contains("active") && typeof cartDrawer.close === "function") {
        cartDrawer.close();
      }

      document.querySelectorAll(".dropdown-menu.show").forEach((menu) => {
        menu.classList.remove("show");
      });

      document.querySelectorAll(".dropdown.show").forEach((dropdown) => {
        dropdown.classList.remove("show");
      });

      document.querySelectorAll("details-modal details[open], .search-details[open]").forEach((detail) => {
        detail.removeAttribute("open");
      });
    }

    onDocumentClick(event) {
      if (!this.isOpen) return;

      const interactiveTarget = event.target.closest(
        "#cart-icon-bubble, .search-icon, details-modal summary, [data-bs-toggle='dropdown']"
      );

      if (interactiveTarget && !interactiveTarget.hasAttribute("data-mobile-menu-trigger")) {
        this.close({ returnFocus: false });
      }
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if ((event.code || "").toUpperCase() === "ESCAPE") {
        event.preventDefault();
        this.close();
        return;
      }

      if ((event.code || "").toUpperCase() !== "TAB") return;

      const focusable = Array.from(this.panel.querySelectorAll(this.focusableSelectors)).filter(
        (element) =>
          !element.hasAttribute("hidden") &&
          !element.closest("[hidden]") &&
          !element.closest(".mobile-menu__pane:not(.is-active)")
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    syncOffset() {
      this.root.style.setProperty("--mobile-menu-offset", "0px");
    }

    expandSearch(options = {}) {
      if (!this.filterControl) return;

      this.filterControl.classList.add("is-expanded");

      if (options.focus && this.filterInput && document.activeElement !== this.filterInput) {
        this.filterInput.focus();
        const valueLength = this.filterInput.value.length;
        this.filterInput.setSelectionRange(valueLength, valueLength);
      }
    }

    collapseSearch(options = {}) {
      if (!this.filterControl) return;
      if (!options.force && this.normalizeSearchValue(this.filterInput?.value || "")) return;

      this.filterControl.classList.remove("is-expanded");

      if (options.blur && this.filterInput && document.activeElement === this.filterInput) {
        this.filterInput.blur();
      }
    }

    resetSearch() {
      if (this.filterInput) {
        this.filterInput.value = "";
      }

      this.panes.forEach((pane) => {
        this.restorePaneSearch(pane);
      });

      this.hideSearchResults();
      this.collapseSearch({ force: true });
    }

    applySearchFilter() {
      const queryTokens = this.tokenizeSearchValue(this.filterInput?.value || "");
      if (!queryTokens.length) {
        this.hideSearchResults();
        return;
      }

      const entries = this.searchEntries.filter(
        (entry) =>
          entry.rootPaneId === this.activeRootPaneId &&
          this.matchesSearchTokens(entry, queryTokens)
      );

      this.renderSearchResults(entries);
    }

    restorePaneSearch(pane) {
      if (!pane) return;

      pane.querySelectorAll("[data-mobile-menu-search-item]").forEach((item) => {
        item.hidden = item.hasAttribute("data-mobile-menu-search-hidden");
      });

      const emptyState = pane.querySelector("[data-mobile-menu-filter-empty]");
      if (emptyState) {
        emptyState.hidden = true;
      }
    }

    getActivePane() {
      return this.paneMap.get(this.activePaneId) || this.root.querySelector("[data-mobile-menu-pane].is-active");
    }

    buildSearchEntry(item) {
      const pane = item.closest("[data-mobile-menu-pane]");
      if (!pane) return null;

      const rootPaneId = pane.getAttribute("data-top-level") || pane.id;
      const label = (item.getAttribute("data-search") || "").trim();
      const baseSearchText = label;
      const searchNormalized = this.normalizeSearchValue(baseSearchText);
      if (!label || !searchNormalized) return null;

      const paneTarget = item.querySelector("[data-mobile-menu-target]");
      const directLink = paneTarget ? null : item.querySelector("a[href]");

      if (!paneTarget && !directLink) return null;

      const trail = this.getPaneTrail(pane.id);
      const rootPaneTitle =
        (this.paneMap.get(rootPaneId)?.getAttribute("data-pane-title") || "").trim();
      const pathSegments = [rootPaneTitle, ...trail, label].filter(Boolean);

      return {
        item,
        label,
        baseSearchText,
        searchNormalized,
        searchTokens: this.tokenizeSearchValue(baseSearchText),
        paneId: pane.id,
        rootPaneId,
        trail,
        actionType: paneTarget ? "pane" : "link",
        targetId: paneTarget?.getAttribute("data-mobile-menu-target") || "",
        href: directLink?.getAttribute("href") || "",
        pathSegments,
        pathKey: this.buildPathKey(pathSegments),
        aliasTerms: [],
      };
    }

    buildSearchEntryPathMap() {
      const pathMap = new Map();

      this.searchEntries.forEach((entry) => {
        if (!entry.pathKey || pathMap.has(entry.pathKey)) return;
        pathMap.set(entry.pathKey, entry);
      });

      return pathMap;
    }

    async enhanceSearchEntriesWithBreadcrumbAliases() {
      if (!this.breadcrumbSearchUrl || typeof window.fetch !== "function") return;

      try {
        const response = await window.fetch(this.breadcrumbSearchUrl, {
          credentials: "same-origin",
        });
        if (!response.ok) return;

        const records = await response.json();
        if (!Array.isArray(records)) return;

        const aliasBuckets = new Map();
        records.forEach((record) => this.collectBreadcrumbAliases(record, aliasBuckets));

        aliasBuckets.forEach((terms, pathKey) => {
          const entry = this.searchEntryPathMap.get(pathKey);
          if (!entry || !terms.size) return;

          entry.aliasTerms = Array.from(
            new Set([...entry.aliasTerms, ...Array.from(terms).filter(Boolean)])
          );
          const enrichedSearchText = [entry.baseSearchText, ...entry.aliasTerms].join(" | ");
          entry.searchNormalized = this.normalizeSearchValue(enrichedSearchText);
          entry.searchTokens = this.tokenizeSearchValue(enrichedSearchText);
          entry._searchTokens = null;

          if (entry.item) {
            entry.item.dataset.searchAliases = entry.aliasTerms.join(" | ");
            entry.item.dataset.searchNormalized = entry.searchNormalized;
          }
        });

        if (this.normalizeSearchValue(this.filterInput?.value || "")) {
          this.applySearchFilter();
        }
      } catch (error) {
        // Leave native row search intact if the optional breadcrumb alias dataset fails to load.
      }
    }

    collectBreadcrumbAliases(record, aliasBuckets) {
      const breadcrumbPath = this.getBreadcrumbRecordPath(record);
      if (breadcrumbPath.length < 2) return;

      let matchedEntry = null;
      let matchedDepth = 0;

      for (let depth = breadcrumbPath.length; depth >= 2; depth -= 1) {
        const pathKey = this.buildPathKey(breadcrumbPath.slice(0, depth));
        const entry = this.searchEntryPathMap.get(pathKey);

        if (entry) {
          matchedEntry = entry;
          matchedDepth = depth;
          break;
        }
      }

      if (!matchedEntry || matchedDepth >= breadcrumbPath.length) return;

      const deeperSegments = breadcrumbPath.slice(matchedDepth).filter(Boolean);
      if (!deeperSegments.length) return;

      const aliasTerms = aliasBuckets.get(matchedEntry.pathKey) || new Set();
      deeperSegments.forEach((segment) => aliasTerms.add(segment));

      if (deeperSegments.length > 1) {
        aliasTerms.add(deeperSegments.join(" "));
        aliasTerms.add(deeperSegments.join(" / "));
      }

      aliasBuckets.set(matchedEntry.pathKey, aliasTerms);
    }

    getBreadcrumbRecordPath(record) {
      if (!record) return [];

      const pathSegments = Array.isArray(record.path)
        ? record.path
        : [record.root, record.level_2, record.level_3, record.level_4, record.level_5];

      return pathSegments
        .filter((segment) => typeof segment === "string")
        .map((segment) => segment.trim())
        .filter(Boolean);
    }

    buildPathKey(segments) {
      return segments
        .map((segment) => this.normalizeSearchValue(segment))
        .filter(Boolean)
        .join("|||");
    }

    matchesSearchTokens(entry, queryTokens) {
      if (!queryTokens.length) return true;

      if (!entry._searchTokens) {
        entry._searchTokens = entry.searchTokens || [];
      }

      return queryTokens.every((queryToken) =>
        entry._searchTokens.some((rowToken) => rowToken.startsWith(queryToken))
      );
    }

    getPaneTrail(paneId) {
      const trail = [];
      let cursorId = paneId;

      while (cursorId) {
        const pane = this.paneMap.get(cursorId);
        if (!pane) break;

        const title = (pane.getAttribute("data-pane-title") || "").trim();
        if (title && !this.rootPaneIds.has(cursorId)) {
          trail.unshift(title);
        }

        cursorId = this.paneParentMap.get(cursorId) || null;
      }

      return trail;
    }

    getPaneStackForTarget(targetId) {
      const stack = [];
      let cursorId = targetId;

      while (cursorId) {
        stack.unshift(cursorId);
        cursorId = this.paneParentMap.get(cursorId) || null;
      }

      return stack;
    }

    renderSearchResults(entries) {
      if (!this.searchResults || !this.searchResultsList || !this.searchResultsEmpty) return;

      this.searchResultsList.innerHTML = "";
      this.searchResults.hidden = false;
      if (this.paneLayer) {
        this.paneLayer.hidden = true;
      }
      this.viewport?.classList.add("is-searching");
      this.searchResultsEmpty.hidden = entries.length !== 0;

      if (!entries.length) {
        return;
      }

      const fragment = document.createDocumentFragment();

      entries.forEach((entry) => {
        const item = document.createElement("li");
        item.className = "mobile-menu__item";

        if (entry.actionType === "pane") {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "mobile-menu__item-row mobile-menu__drilldown";
          button.setAttribute("data-mobile-menu-search-target", entry.targetId);
          button.setAttribute("aria-label", `Open ${entry.label}`);

          const labelSpan = document.createElement("span");
          labelSpan.className = "mobile-menu__link-text";
          labelSpan.textContent = entry.label;
          button.appendChild(labelSpan);

          const icon = document.createElement("span");
          icon.className = "mobile-menu__icon";
          icon.setAttribute("aria-hidden", "true");
          icon.innerHTML = "<svg viewBox='0 0 5 8' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M1 1L4 4L1 7' stroke='currentColor' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/></svg>";
          button.appendChild(icon);
          item.appendChild(button);
        } else {
          const link = document.createElement("a");
          link.className = "mobile-menu__link mobile-menu__link--single";
          link.href = entry.href;
          link.setAttribute("data-mobile-menu-search-link", "true");

          const labelSpan = document.createElement("span");
          labelSpan.textContent = entry.label;
          link.appendChild(labelSpan);
          item.appendChild(link);
        }

        fragment.appendChild(item);
      });

      this.searchResultsList.appendChild(fragment);
      const searchResultsScroll = this.searchResults.querySelector(".mobile-menu__search-results-scroll");
      if (searchResultsScroll) {
        searchResultsScroll.scrollTop = 0;
      }
    }

    hideSearchResults() {
      if (!this.searchResults || !this.searchResultsList || !this.searchResultsEmpty) return;

      this.searchResults.hidden = true;
      if (this.paneLayer) {
        this.paneLayer.hidden = false;
      }
      this.viewport?.classList.remove("is-searching");
      this.searchResultsEmpty.hidden = true;
      this.searchResultsList.innerHTML = "";
    }

    openSearchResultPane(targetId) {
      if (!targetId) return;

      const nextPaneStack = this.getPaneStackForTarget(targetId);
      if (!nextPaneStack.length) return;

      const topLevelPaneId = nextPaneStack[0];
      this.resetSearch();
      this.navigateToPane(targetId, {
        direction: "forward",
        topLevelPaneId,
        nextPaneStack,
        resetAllScroll: true,
      });
    }

    normalizeSearchValue(value) {
      return value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .replace(/\s+/g, " ");
    }

    tokenizeSearchValue(value) {
      const normalized = this.normalizeSearchValue(value);
      return normalized ? normalized.split(" ").filter(Boolean) : [];
    }

    getRootPaneId(paneId) {
      const pane = this.paneMap.get(paneId);
      return pane?.getAttribute("data-top-level") || paneId;
    }
  }

  const mobileMenuInstances = [];

  function initMobileMenus() {
    document.querySelectorAll("[data-mobile-menu-root]").forEach((root) => {
      if (root.dataset.mobileMenuReady === "true") return;
      root.dataset.mobileMenuReady = "true";
      mobileMenuInstances.push(new MobileMenuController(root));
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initMobileMenus);
  } else {
    initMobileMenus();
  }

  // Before the browser freezes this page into bfcache, close any open menu so
  // the snapshot is already in the closed state. This prevents the ~240ms
  // visibility flash that occurs when closeImmediate() is called on pageshow
  // (too late — the restored open DOM has already been painted by then).
  window.addEventListener("pagehide", function (event) {
    if (!event.persisted) return;
    mobileMenuInstances.forEach(function (menu) {
      if (menu.isOpen || menu.isClosing) {
        menu.closeImmediate();
      }
    });
  });

  // Safety net: if a browser restores from bfcache without having honoured the
  // pagehide cleanup (or if the menu somehow reopened during freeze), reset it
  // here too. closeImmediate() is idempotent when the menu is already closed.
  window.addEventListener("pageshow", function (event) {
    if (!event.persisted) return;
    mobileMenuInstances.forEach(function (menu) {
      if (menu.isOpen || menu.isClosing) {
        menu.closeImmediate();
      }
    });
  });
})();
